import { Suspense, lazy, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './AuthContext';
import { BillingProvider, useBilling } from './contexts/BillingContext';
import { InitialImportStatusProvider } from './contexts/InitialImportStatusContext';
import { AiConsentProvider } from './contexts/AiConsentContext';
import ErrorBoundary from './components/shared/ErrorBoundary';
import ProtectedRoute from './ProtectedRoute';
import PublicOnlyRoute from './PublicOnlyRoute';
import GlobalProgressBar from './components/ui/GlobalProgressBar';
import { ToastProvider } from './contexts/ToastContext';
import { MonthProvider, useMonthContext } from './contexts/MonthContext';
import BillingPaymentSuccessOverlay from './components/billing/BillingPaymentSuccessOverlay';
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import SessionExpired from './pages/auth/SessionExpired';
import CheckEmail from './CheckEmail';
import Verify2FA from './Verify2FA';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import ResetSuccess from './ResetSuccess';
import AcceptInvite from './AcceptInvite';
import AuthCallback from './AuthCallback';
import GlobalFAB from './components/ui/GlobalFAB';
import GlobalInitialImportBanner from './components/shell/GlobalInitialImportBanner';
import RouteLoadingFallback from './components/shared/RouteLoadingFallback';
import LoadingSpinner from './components/shared/LoadingSpinner';
import RouteTransitionTracker from './components/telemetry/RouteTransitionTracker';
import { hasInsightsCapability, isInsightsUiHintEnabled } from './utils/insightsAccess';
import { hasReportsCapability } from './utils/reportsAccess';
import useCurrentMonthKey from './hooks/useCurrentMonthKey';
import {
  loadExpenseTracker,
  loadBudgetsScreen,
  loadAccountsScreen,
  loadCategoriesScreen,
  loadHouseholdScreen,
  loadIncomeScreen,
  loadNetWorthScreen,
  loadTransactionsScreen,
  loadSettingsScreen,
  loadReportsScreen,
  loadInsightsScreen,
  loadInsightsDebtOptimizerScreen,
} from './navigation/desktopRouteBundles';
import { loadInsightsMobileScreen } from './navigation/insightsRouteBundles';
import { isPhoneExperience, resolveExperiencePath } from './navigation/phoneExperience';
import { useDesktopRealtimeInvalidation } from './hooks/useDashboardRealtimeRefresh';

const loadOnboarding = () => import('./Onboarding');
const loadMobileApp = () => import('./screens/mobile/MobileApp');
const loadCheckoutReturn = () => import('./components/billing/CheckoutReturn');
const loadMobileBankConnectBridge = () => import('./MobileBankConnectBridge');
const loadMastercardConnectCallback = () => import('./MastercardConnectCallback');
const loadMonthlyReportPromptHost = () => import(
  /* webpackChunkName: "reports" */ './components/reports/MonthlyReportPromptHost'
);
const loadLegalDocumentPage = () => import('./pages/legal/LegalDocumentPage');
const BankUpdatesRedirect = lazy(() => import('./pages/help/BankUpdatesRedirect'));
const loadInteractionStateGallery = () => import('./dev/InteractionStateGallery');
const Onboarding = lazy(loadOnboarding);
const ExpenseTracker = lazy(loadExpenseTracker);
const MobileApp = lazy(loadMobileApp);
const PhonePresentation = lazy(() => import('./components/mobile/PhonePresentation'));

const ExperienceBoundary = ({ children }) => {
  const location = useLocation();
  const phone = isPhoneExperience();
  const destination = resolveExperiencePath(location.pathname, phone);
  if (destination !== location.pathname) {
    return <Navigate replace to={{ pathname: destination, search: location.search, hash: location.hash }} state={location.state} />;
  }
  const nativeBridge = location.pathname === '/mobile-connect-bank' || location.pathname === '/bank-connect/mastercard/callback';
  if (!phone || nativeBridge) return children;
  return <Suspense fallback={<RouteLoadingFallback label="Loading page" surface="public" />}><PhonePresentation>{children}</PhonePresentation></Suspense>;
};
const CheckoutReturn = lazy(loadCheckoutReturn);
const MobileBankConnectBridge = lazy(loadMobileBankConnectBridge);
const MastercardConnectCallback = lazy(loadMastercardConnectCallback);
const BudgetsScreen = lazy(loadBudgetsScreen);
const AccountsScreen = lazy(loadAccountsScreen);
const CategoriesScreen = lazy(loadCategoriesScreen);
const HouseholdScreen = lazy(loadHouseholdScreen);
const IncomeScreen = lazy(loadIncomeScreen);
const NetWorthScreen = lazy(loadNetWorthScreen);
const TransactionsScreen = lazy(loadTransactionsScreen);
const SettingsScreen = lazy(loadSettingsScreen);
const ReportsScreen = lazy(loadReportsScreen);
const MonthlyReportPromptHost = lazy(loadMonthlyReportPromptHost);
const LegalDocumentPage = lazy(loadLegalDocumentPage);
const InteractionStateGallery = lazy(loadInteractionStateGallery);
const ReviewQueuePreview = process.env.NODE_ENV === 'development'
  ? lazy(() => import('./dev/ReviewQueuePreview'))
  : null;
const InsightsScreen = lazy(loadInsightsScreen);
const InsightsDebtOptimizerScreen = lazy(loadInsightsDebtOptimizerScreen);
const InsightsMobileScreen = lazy(loadInsightsMobileScreen);
const visualFixturesEnabled = process.env.REACT_APP_ENABLE_VISUAL_FIXTURES === 'true';

const InteractionSurfaceScope = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const surface = pathname === '/m' || pathname.startsWith('/m/')
      ? 'mobile-web'
      : 'desktop-public';
    document.body.dataset.vyloInteractionSurface = surface;

    return () => {
      if (document.body.dataset.vyloInteractionSurface === surface) {
        delete document.body.dataset.vyloInteractionSurface;
      }
    };
  }, [pathname]);

  return null;
};

const isMobileDevice = isPhoneExperience;

// Component to handle dashboard routing based on device
export const DashboardRoute = () => {
  if (isMobileDevice()) {
    return <Navigate to="/m/home" replace />;
  }
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <ExpenseTracker />
    </Suspense>
  );
};

export const AppThemeBoundary = ({ children }) => {
  const { pathname } = useLocation();
  return <ThemeProvider pathname={pathname}>{children}</ThemeProvider>;
};

const DesktopRealtimeInvalidationBridge = () => {
  const { authenticatedFetch, household, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const householdId = household?.id;
  const isMobileWebRoute = pathname === '/m' || pathname.startsWith('/m/');

  useDesktopRealtimeInvalidation({
    authenticatedFetch,
    enabled: isAuthenticated === true && Boolean(householdId) && !isMobileWebRoute,
    householdId,
  });

  return null;
};

const InsightsBundleHint = () => {
  const { features } = useAuth();

  useEffect(() => {
    if (!hasInsightsCapability(features) || !isInsightsUiHintEnabled()) return;
    loadInsightsScreen();
    loadInsightsDebtOptimizerScreen();
    loadInsightsMobileScreen();
  }, [features]);

  return null;
};

const MonthlyReportPromptGate = () => {
  const { features } = useAuth();

  if (isMobileDevice() || !hasReportsCapability(features)) return null;

  return (
    <Suspense fallback={null}>
      <MonthlyReportPromptHost />
    </Suspense>
  );
};

const InsightsAccessRoute = ({ mobile = false, variant = 'overview' }) => {
  const { loading, features } = useAuth();

  if (loading) {
    return <RouteLoadingFallback label="Loading insights" surface="shell" height="screen" routeKind="insights" />;
  }

  if (!hasInsightsCapability(features)) {
    return <Navigate to={mobile ? '/m/home' : '/dashboard'} replace />;
  }

  return (
    <Suspense fallback={<RouteLoadingFallback label="Loading insights" surface="shell" height="screen" routeKind="insights" />}>
      {mobile ? <InsightsMobileScreen /> : variant === 'debt' ? <InsightsDebtOptimizerScreen /> : <InsightsScreen />}
    </Suspense>
  );
};

const LazyPage = ({
  component: Component,
  routeFallbackLabel = 'Loading page',
  routeFallbackSurface = 'shell',
  routeFallbackHeight = 'screen',
  routeFallbackKind = 'generic',
  ...props
}) => (
  <Suspense
    fallback={(
      <RouteLoadingFallback
        label={routeFallbackLabel}
        surface={routeFallbackSurface}
        height={routeFallbackHeight}
        routeKind={routeFallbackKind}
      />
    )}
  >
    <Component {...props} />
  </Suspense>
);

const ReportsAccessRoute = () => {
  const { loading, features } = useAuth();

  if (loading) {
    return <RouteLoadingFallback label="Loading reports" surface="shell" height="screen" routeKind="reports" />;
  }

  if (!hasReportsCapability(features)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LazyPage component={ReportsScreen} routeFallbackLabel="Loading reports" routeFallbackKind="reports" />;
};

// Keeps BillingContext in sync when AuthContext.billingStatus updates (e.g. after token refresh).
// Must render inside BillingProvider so useBilling() resolves the live context.
const BillingSyncer = ({ authBillingStatus }) => {
  const { seedFromAuthMe } = useBilling();
  useEffect(() => {
    if (
      authBillingStatus && (
        authBillingStatus.billingState !== 'not_provisioned' ||
        authBillingStatus.accessState !== 'not_provisioned'
      )
    ) {
      seedFromAuthMe(authBillingStatus);
    }
  }, [authBillingStatus, seedFromAuthMe]);
  return null;
};

// BillingProvider needs access to auth state, so it lives inside AuthProvider.
const BillingBridge = ({ children }) => {
  const { authenticatedFetch, onboardingComplete, billingStatus: authBillingStatus } = useAuth();
  return (
      <BillingProvider
        authenticatedFetch={authenticatedFetch}
        onboardingComplete={onboardingComplete}
        initialBillingStatus={authBillingStatus}
      >
      <InsightsBundleHint />
      <BillingSyncer authBillingStatus={authBillingStatus} />
      {children}
    </BillingProvider>
  );
};

export const ProtectedThemedAppLayout = () => {
  return (
      <BillingBridge>
        <ProtectedRoute>
          <InitialImportStatusProvider>
            {!isMobileDevice() && <DesktopRealtimeInvalidationBridge />}
            {!isMobileDevice() && <GlobalFAB />}
            <GlobalInitialImportBanner />
            <MonthlyReportPromptGate />
            <Outlet />
            <BillingPaymentSuccessOverlay />
          </InitialImportStatusProvider>
        </ProtectedRoute>
      </BillingBridge>
  );
};

const ScopedMonthProvider = ({ children }) => {
  const { user, household } = useAuth();
  const userId = user?.id || user?.userId || null;
  const householdId = household?.id || user?.householdId || null;
  const scopeKey = userId && householdId ? `${userId}:${householdId}` : null;

  return (
    <MonthProvider scopeKey={scopeKey}>
      {children}
    </MonthProvider>
  );
};

export const MonthRouteScopeGuard = () => {
  const currentMonthKey = useCurrentMonthKey();
  const location = useLocation();
  const { selectedMonth, setSelectedMonth } = useMonthContext();
  const preservesReportMonth = location.state?.fromReport === true
    && ['/transactions', '/budgets', '/m/activity', '/m/spend'].includes(location.pathname);

  useLayoutEffect(() => {
    if (isPhoneExperience() || preservesReportMonth) return;
    if (selectedMonth !== currentMonthKey) setSelectedMonth(currentMonthKey);
  }, [currentMonthKey, location.key, preservesReportMonth, selectedMonth, setSelectedMonth]);

  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AiConsentProvider>
        <ScopedMonthProvider>
          <Router useTransitions={false}>
            <InteractionSurfaceScope />
            <RouteTransitionTracker />
            <MonthRouteScopeGuard />
            <GlobalProgressBar />
            <ToastProvider>
              <AppThemeBoundary>
              <ExperienceBoundary>
              <Routes>
                <Route path="/login" element={<PublicOnlyRoute><SignIn /></PublicOnlyRoute>} />
                <Route path="/session-expired" element={<SessionExpired />} />
                <Route path="/signup" element={<PublicOnlyRoute><SignUp /></PublicOnlyRoute>} />
                <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
                <Route path="/reset-password" element={<PublicOnlyRoute><ResetPassword /></PublicOnlyRoute>} />
                <Route path="/reset-success" element={<ResetSuccess />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route path="/auth-callback" element={<AuthCallback />} />
                <Route
                  path="/mobile-connect-bank"
                  element={<LazyPage component={MobileBankConnectBridge} routeFallbackLabel="Opening bank connection" routeFallbackSurface="public" />}
                />
                <Route
                  path="/bank-connect/mastercard/callback"
                  element={
                      <LazyPage component={MastercardConnectCallback} routeFallbackLabel="Completing bank connection" routeFallbackSurface="public" />
                  }
                />
                <Route path="/check-email" element={<CheckEmail />} />
                <Route path="/verify-email" element={<CheckEmail />} />
                <Route path="/verify-2fa" element={<Verify2FA />} />
                <Route
                  path="/help/bank-updates"
                  element={<LazyPage component={BankUpdatesRedirect} routeFallbackLabel="Opening Vylo Help" routeFallbackSurface="public" routeFallbackKind="legal" />}
                />
                <Route
                  path="/terms-of-use"
                  element={(
                    <LazyPage
                      component={LegalDocumentPage}
                      documentKey="terms"
                      routeFallbackLabel="Loading terms of use"
                      routeFallbackSurface="public"
                      routeFallbackKind="legal"
                    />
                  )}
                />
                <Route
                  path="/privacy-policy"
                  element={(
                    <LazyPage
                      component={LegalDocumentPage}
                      documentKey="privacy"
                      routeFallbackLabel="Loading privacy policy"
                      routeFallbackSurface="public"
                      routeFallbackKind="legal"
                    />
                  )}
                />
                <Route
                  path="/onboarding"
                  element={
                      <BillingBridge>
                        <ProtectedRoute requireOnboarding={false}>
                          <InitialImportStatusProvider>
                            <GlobalInitialImportBanner />
                            <LazyPage
                              component={Onboarding}
                              routeFallbackLabel="Loading onboarding"
                              routeFallbackSurface="onboarding"
                              routeFallbackHeight="screen"
                              routeFallbackKind="onboarding"
                            />
                          </InitialImportStatusProvider>
                        </ProtectedRoute>
                      </BillingBridge>
                  }
                />
                <Route path="/" element={<ProtectedThemedAppLayout />}>
                  <Route index element={<DashboardRoute />} />
                  <Route path="dashboard" element={<DashboardRoute />} />
                  <Route path="insights" element={<InsightsAccessRoute />} />
                  <Route path="insights/debt" element={<InsightsAccessRoute variant="debt" />} />
                  <Route path="transactions" element={<LazyPage component={TransactionsScreen} routeFallbackLabel="Loading transactions" routeFallbackKind="transactions" />} />
                  <Route path="income" element={<LazyPage component={IncomeScreen} routeFallbackLabel="Loading income" routeFallbackKind="income" />} />
                  <Route path="net-worth" element={<LazyPage component={NetWorthScreen} routeFallbackLabel="Loading net worth" routeFallbackKind="net-worth" />} />
                  <Route path="budgets" element={<LazyPage component={BudgetsScreen} routeFallbackLabel="Loading budgets" routeFallbackKind="budgets" />} />
                  <Route path="categories" element={<LazyPage component={CategoriesScreen} routeFallbackLabel="Loading categories" routeFallbackKind="categories" />} />
                  <Route path="reports" element={<ReportsAccessRoute />} />
                  <Route path="accounts" element={<LazyPage component={AccountsScreen} routeFallbackLabel="Loading accounts" routeFallbackKind="accounts" />} />
                  <Route path="household" element={<LazyPage component={HouseholdScreen} routeFallbackLabel="Loading household" routeFallbackKind="household" />} />
                  <Route path="settings" element={<LazyPage component={SettingsScreen} routeFallbackLabel="Loading settings" routeFallbackKind="settings" />} />
                  <Route path="billing/success" element={<LazyPage component={CheckoutReturn} routeFallbackLabel="Confirming payment" routeFallbackKind="billing" />} />
                  <Route path="m/*" element={<LazyPage component={MobileApp} />} />
                </Route>
                <Route
                  path="/__fixtures__/interaction-states"
                  element={visualFixturesEnabled ? (
                    <LazyPage
                      component={InteractionStateGallery}
                      routeFallbackLabel="Loading interaction gallery"
                      routeFallbackSurface="public"
                      routeFallbackKind="generic"
                    />
                  ) : <Navigate to="/" replace />}
                />
                <Route
                  path="/__fixtures__/review-queue"
                  element={visualFixturesEnabled && process.env.NODE_ENV === 'development' ? (
                    <LazyPage
                      component={ReviewQueuePreview}
                      routeFallbackLabel="Loading review preview"
                      routeFallbackSurface="public"
                      routeFallbackKind="generic"
                    />
                  ) : <Navigate to="/" replace />}
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </ExperienceBoundary>
              </AppThemeBoundary>
            </ToastProvider>
          </Router>
        </ScopedMonthProvider>
        </AiConsentProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
